/**
 * Security Advisory Queries
 *
 * Pre-built queries for critical CVEs and security vulnerabilities.
 * Organized by CVE ID for easy lookup.
 *
 * To add new CVE: Add query object to SECURITY_ADVISORY_QUERIES array.
 * Query will automatically appear in defaultQueries.ts export.
 */

import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { QueryFolders } from './folders';

export const SECURITY_ADVISORY_QUERIES: SavedQuery[] = [
  {
    id: 'cve-2025-55182-react-nextjs-rce',
    name: 'CVE-2025-55182: React NextJS RCE',
    type: 'table',
    description:
      'Find vulnerabilities related to the critical React NextJS Remote Code Execution vulnerability (CVE-2025-55182)',
    folderId: QueryFolders.SECURITY_ADVISORIES,
    query: {
      node: {
        labels: ['Risk'],
        filters: [],
        relationships: [
          {
            label: 'INSTANCE_OF',
            target: {
              labels: ['Vulnerability'],
              filters: [
                {
                  field: 'id',
                  operator: '=',
                  value: 'CVE-2025-55182',
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 100,
    },
  },
  {
    id: 'cve-2024-3400-pan-os-globalprotect-command-injection-rce',
    name: 'CVE-2024-3400: PAN-OS GlobalProtect Command Injection RCE',
    type: 'table',
    description:
      'Find vulnerabilities related to the critical command injection in Palo Alto Networks PAN-OS GlobalProtect Gateway, allowing unauthenticated remote code execution. CVSS 10.0.',
    folderId: QueryFolders.SECURITY_ADVISORIES,
    query: {
      node: {
        labels: ['Risk'],
        filters: [],
        relationships: [
          {
            label: 'INSTANCE_OF',
            target: {
              labels: ['Vulnerability'],
              filters: [
                {
                  field: 'id',
                  operator: '=',
                  value: 'CVE-2024-3400',
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 100,
    },
  },
  {
    id: 'cve-2023-46747-f5-big-ip-authentication-bypass-rce',
    name: 'CVE-2023-46747: F5 BIG-IP Authentication Bypass RCE',
    type: 'table',
    description:
      'Find vulnerabilities related to the critical authentication bypass in F5 BIG-IP Configuration Utility (TMUI) via AJP request smuggling, allowing unauthenticated remote code execution. Discovered by Praetorian Security.',
    folderId: QueryFolders.SECURITY_ADVISORIES,
    query: {
      node: {
        labels: ['Risk'],
        filters: [],
        relationships: [
          {
            label: 'INSTANCE_OF',
            target: {
              labels: ['Vulnerability'],
              filters: [
                {
                  field: 'id',
                  operator: '=',
                  value: 'CVE-2023-46747',
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 100,
    },
  },
  {
    id: 'cve-2023-27997-fortios-ssl-vpn-heap-overflow-rce',
    name: 'CVE-2023-27997: FortiOS SSL-VPN Heap Overflow RCE',
    type: 'table',
    description:
      'Find vulnerabilities related to the critical heap-based buffer overflow in Fortinet FortiOS SSL-VPN, allowing pre-authentication remote code execution. CVSS 9.8.',
    folderId: QueryFolders.SECURITY_ADVISORIES,
    query: {
      node: {
        labels: ['Risk'],
        filters: [],
        relationships: [
          {
            label: 'INSTANCE_OF',
            target: {
              labels: ['Vulnerability'],
              filters: [
                {
                  field: 'id',
                  operator: '=',
                  value: 'CVE-2023-27997',
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 100,
    },
  },
  {
    id: 'cve-2023-22527-atlassian-confluence-ognl-injection-rce',
    name: 'CVE-2023-22527: Atlassian Confluence OGNL Injection RCE',
    type: 'table',
    description:
      'Find vulnerabilities related to the critical OGNL injection in Atlassian Confluence Data Center and Server, allowing unauthenticated remote code execution. CVSS 10.0.',
    folderId: QueryFolders.SECURITY_ADVISORIES,
    query: {
      node: {
        labels: ['Risk'],
        filters: [],
        relationships: [
          {
            label: 'INSTANCE_OF',
            target: {
              labels: ['Vulnerability'],
              filters: [
                {
                  field: 'id',
                  operator: '=',
                  value: 'CVE-2023-22527',
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 100,
    },
  },
  {
    id: 'cve-2022-22965-spring4shell-rce',
    name: 'CVE-2022-22965: Spring4Shell RCE',
    type: 'table',
    description:
      'Find vulnerabilities related to the critical Spring Framework RCE via data binding on JDK 9+ (Spring4Shell). Allows remote code execution through ClassLoader manipulation.',
    folderId: QueryFolders.SECURITY_ADVISORIES,
    query: {
      node: {
        labels: ['Risk'],
        filters: [],
        relationships: [
          {
            label: 'INSTANCE_OF',
            target: {
              labels: ['Vulnerability'],
              filters: [
                {
                  field: 'id',
                  operator: '=',
                  value: 'CVE-2022-22965',
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 100,
    },
  },
  {
    id: 'cve-2021-34473-microsoft-exchange-proxyshell-rce',
    name: 'CVE-2021-34473: Microsoft Exchange ProxyShell RCE',
    type: 'table',
    description:
      'Find vulnerabilities related to the critical pre-authentication path confusion in Microsoft Exchange Server (ProxyShell), enabling remote code execution when chained. Actively exploited.',
    folderId: QueryFolders.SECURITY_ADVISORIES,
    query: {
      node: {
        labels: ['Risk'],
        filters: [],
        relationships: [
          {
            label: 'INSTANCE_OF',
            target: {
              labels: ['Vulnerability'],
              filters: [
                {
                  field: 'id',
                  operator: '=',
                  value: 'CVE-2021-34473',
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 100,
    },
  },
];
